//
// README - Beta Component
//
// This component is marked "beta" and will go through some iterations
// before landing at a final solution. Please reach out to the Cadence team
// if you intend to use this in the meantime and we'll help you out!
//

import React, { useRef } from 'react';
import classNames from 'classnames';

import { RCText, useMediaQuery } from '..';
import { RCImage, RCImageProps } from '../RCImage/RCImage';
import {
  RCModalBase,
  RCModalBaseRootProps,
  RCModalFormProps,
  useRCModalBaseContext,
} from '../internalComponents/RCModalBase/RCModalBase';
import { RCModalActionsProps } from '../internalComponents/RCModalBase/RCModalBaseActions';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';

type ElementRef = React.MutableRefObject<HTMLElement | null>;

/**
 * Closes the `RCModal` that contains the calling component.
 *
 * For content rendered inside a modal that should dismiss it once its work is done — a link that
 * navigates away, a form that submits. Saves threading a close callback down through the modal's
 * children.
 *
 * Closing this way runs the same path as Escape or the close button, so the modal's
 * `onOpenChange` still fires.
 *
 * Outside a modal, `close` is a no-op — safe in a component rendered both inside and outside one.
 *
 * ```tsx
 * function SaveAction() {
 *   const { close } = useRCModal();
 *   return <RCButton onClick={() => { save(); close(); }}>Save</RCButton>;
 * }
 * ```
 */
export function useRCModal() {
  return useRCModalBaseContext();
}

interface RCModalProps {
  /** Optionally provide a trigger to have the modal handle onClick behavior and state changes. */
  trigger?: JSX.Element;
  /** Can be used to control the state of the modal when paired with `onOpenChange`. */
  isOpen?: RCModalBaseRootProps['isOpen'];
  /** Can be used to control the state of the modal when paired with `isOpen`.
   *  Alternatively, can be used as a callback to track state changes.
  */
  onOpenChange?: RCModalBaseRootProps['onOpenChange'];
  /** The title of the modal to be rendered in the header OR The id of the preferred title element (hides the default title in favor of the referenced element). */
  title: string | { 'aria-labelledby': string; }
  titleOptions?: {
    /** Allows for centering of the default title. */
    centered?: boolean;
  }
  /** Allows for rendering an image above the title. */
  headerImage?: RCImageProps;
  /** Actions to be appended at the end of the modal, below the scrollable content area. */
  actions?: RCModalActionsProps;
  /** Wraps the modal's content in a `<form>` that the `primary` action submits, so Enter in a text
   * input and `required` validation work too.
   */
  form?: RCModalFormProps;
  /** Allows you to control where the focus ring is placed when opening the modal (with keyboard).
   *  Defaults to the modal's `'body'` content.
   *  Use `'close'` if your modal's content is lengthy and the only interactive content appears below the fold.
   *
   *  Note: for setting initial focus on an action, use `actions` instead.
   */
  initialFocus?: 'body' | 'close' | ElementRef;
}

export function RCModal({
  trigger = undefined,
  isOpen = undefined,
  onOpenChange = undefined,
  title,
  titleOptions = undefined,
  headerImage = undefined,
  actions = undefined,
  form = undefined,
  initialFocus = 'body',
  children = undefined,
}: React.PropsWithChildren<RCModalProps>) {
  const closeButtonRef = useRef(null);

  const focus = React.useMemo(() => {
    if (initialFocus === 'close') return closeButtonRef;
    if (initialFocus === 'body') return 0;
    return initialFocus;
  }, [initialFocus, closeButtonRef]);

  const isMobile = useMediaQuery('mobile');
  const withImage = !!headerImage;
  const titleOverride = typeof title !== 'string' ? title['aria-labelledby'] : undefined;
  const hasStringTitle = typeof title === 'string';
  return (
    <RCModalBase.Root
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      initialFocus={focus}
      role="dialog"
      useDismissProps={{ outsidePressEvent: 'click' }}
    >
      {trigger && <RCModalBase.Trigger>{trigger}</RCModalBase.Trigger>}
      <RCModalBase.Content
        className="rc-modal"
        position={isMobile ? 'bottom' : undefined}
        aria-labelledby={titleOverride}
      >
        <div className="rc-modal__content">
          <div className={classNames(
            'rc-modal__header',
            { 'rc-modal__header--no-title': !!titleOverride },
            { 'rc-modal__header--with-image': withImage },
          )}>
            {withImage &&
              <div className="rc-modal__header-image">
                <RCImage {...headerImage} />
              </div>
            }
            {hasStringTitle &&
              <RCModalBase.Heading
                className={classNames(
                  'rc-modal__header-content',
                  { 'rc-modal__header-content--centered': titleOptions?.centered },
                )}
              >
                <RCText.Title size="600" htmlTag="span">
                  {title}
                </RCText.Title>
              </RCModalBase.Heading>
            }
          </div>
          <RCModalBody inline={withImage}>
            {form ? <RCModalBase.Form {...form}>{children}</RCModalBase.Form> : children}
          </RCModalBody>
          {actions &&
            <div className="rc-modal__footer">
              <RCModalBase.Actions
                {...actions}
                align={actions.align ?? { desktop: 'split', mobile: 'stretch' }}
              />
            </div>
          }
          <RCModalBase.CloseButton
            ref={closeButtonRef}
            variant={withImage ? 'light' : 'transparent'}
          />
        </div>
      </RCModalBase.Content>
    </RCModalBase.Root>
  );
}

function RCModalBody({ inline = false, children }) {
  const observerTop = useIntersectionObserver();
  const observerBottom = useIntersectionObserver();

  return (
    <div
      className={classNames(
        'rc-modal__body',
        { 'rc-modal__body--inline': inline },
        { 'rc-modal__body--shadow-top': observerTop?.entry && !observerTop?.entry?.isIntersecting },
        { 'rc-modal__body--shadow-bottom': observerBottom?.entry && !observerBottom?.entry?.isIntersecting },
      )}
    >
      <div className="rc-modal__body__content">
        <div ref={inline ? null : observerTop.setTrackedRef} data-observer={inline ? null : true} />
        {children}
        <div ref={inline ? null : observerBottom.setTrackedRef} data-observer={inline ? null : true} />
      </div>
    </div>
  );
}
